import { useCallback, useEffect, useRef, useState } from "react";
import { invoke } from "@tauri-apps/api/core";
import { Mic, RotateCcw } from "lucide-react";
import { Button } from "@link/ui";
import { checkMicrophoneAccess, mediaPermissionDeniedMessage } from "../../lib/media-permissions";
import { useMediaSession } from "../../state/MediaSession";

interface PermissionStatus { microphone: boolean | null; camera: boolean | null }
const savedChoice = (value: boolean | null) => value === null ? "Not set up" : value ? "Allowed in Link" : "Blocked in Link";

export function VoiceVideoSettings() {
  const desktop = (globalThis as { __LINK_DESKTOP__?: { engine?: string; platform?: string } }).__LINK_DESKTOP__;
  const nativeWindows = desktop?.engine === "tauri" && desktop.platform === "windows";
  const media = useMediaSession();
  const [permissions, setPermissions] = useState<PermissionStatus | null>(null);
  const [bridgeError, setBridgeError] = useState<string | null>(null);
  const [bridgeDetail, setBridgeDetail] = useState<string | null>(null);
  const [captureDetail, setCaptureDetail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const mounted = useRef(true);
  const checking = useRef(false);
  const refresh = useCallback(async () => {
    if (!nativeWindows) return;
    try {
      const status = await invoke<PermissionStatus>("media_permissions", { operation: "status" });
      if (mounted.current) { setPermissions(status); setBridgeError(null); setBridgeDetail(null); }
    } catch (error) {
      if (mounted.current) {
        setBridgeError("Link couldn't read its permission settings. Restart the latest desktop app and try again.");
        // This command only reads permission choices. Surface its bridge failure
        // in local development instead of misdiagnosing it as OS capture denial.
        if (import.meta.env.DEV) setBridgeDetail((error instanceof Error ? error.message : String(error)).slice(0, 400));
      }
    }
  }, [nativeWindows]);
  useEffect(() => {
    mounted.current = true;
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => { mounted.current = false; window.removeEventListener("focus", onFocus); };
  }, [refresh]);
  async function check() {
    if (checking.current) return;
    checking.current = true; setBusy(true); setResult(null); setCaptureDetail(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("Microphone access is unavailable here. Open Link over HTTPS or localhost.");
      await checkMicrophoneAccess(navigator.mediaDevices);
      if (mounted.current) setResult({ ok: true, text: "Microphone access works. The check has ended and your microphone is off. You can try your call again." });
    } catch (error) {
      const name = error instanceof Error ? error.name : "";
      const text = name === "NotAllowedError" ? mediaPermissionDeniedMessage(false, desktop)
        : name === "NotFoundError" ? "No microphone was found. Connect one and try again."
        : name === "NotReadableError" ? "The microphone couldn't be opened. Check whether another app is using it, then try again."
        : error instanceof Error ? error.message : "Couldn't check microphone access.";
      if (mounted.current) {
        setResult({ ok: false, text });
        if (import.meta.env.DEV) setCaptureDetail((error instanceof Error ? `${error.name}: ${error.message}` : String(error)).slice(0, 400));
      }
    } finally {
      checking.current = false;
      if (mounted.current) { setBusy(false); void refresh(); }
    }
  }
  async function reset() {
    setResult(null);
    try {
      await invoke("media_permissions", { operation: "reset" });
      if (mounted.current) setResult({ ok: true, text: "Confirm the reset in Link's dialog. After Link reloads, return here and choose Enable microphone & check to set it up again." });
    } catch {
      if (mounted.current) setResult({ ok: false, text: "Couldn't open the permission reset. Restart the latest desktop app and try again." });
    }
  }
  const inCall = media.connected || media.connecting || media.activeCall !== null || media.outgoingCall !== null;
  return <section className="max-w-2xl space-y-6">
    <header><h2 className="text-2xl font-semibold text-cloud-800">Voice &amp; video</h2><p className="mt-1 text-sm text-cloud-500">Check your microphone before a call. Your camera only turns on when you choose video.</p></header>
    <div className="space-y-4 rounded-2xl border border-cloud-200 bg-cloud-50/50 p-5">
      <div className="flex items-center gap-2 text-base font-semibold text-cloud-800"><Mic className="h-5 w-5" />Microphone</div>
      <p className="text-sm text-cloud-500">{nativeWindows && "Microphone permission is saved during first-time setup, so voice calls don't interrupt you with a prompt. "}This briefly checks access, then stops the microphone. Nothing is played back, recorded, or sent to anyone.</p>
      <Button onClick={() => void check()} disabled={busy || inCall}>{busy ? "Waiting for microphone…" : nativeWindows && permissions?.microphone === null ? "Enable microphone & check" : "Check microphone"}</Button>
      {inCall && <p className="text-xs text-cloud-500">Leave the current call before running a separate microphone check.</p>}
    </div>
    {nativeWindows && <div className="space-y-4 rounded-2xl border border-cloud-200 p-5">
      <h3 className="font-semibold text-cloud-800">Permissions on this computer</h3>
      {permissions && <dl className="grid grid-cols-[1fr_auto] gap-3 text-sm"><dt>Microphone</dt><dd>{savedChoice(permissions.microphone)}</dd><dt>Camera</dt><dd>{savedChoice(permissions.camera)}</dd></dl>}
      {bridgeError && <p role="alert" className="text-sm text-rose-500">{bridgeError}</p>}
      {import.meta.env.DEV && bridgeDetail && <p className="break-words text-xs text-cloud-500">Desktop diagnostic: {bridgeDetail}</p>}
      <p className="text-sm text-cloud-500">If you chose “Don't allow,” reset that choice here. Resetting reloads Link and ends any call; your sign-in and messages stay intact. It doesn't grant access or change Windows privacy settings.</p>
      <Button variant="outline" onClick={() => void reset()} disabled={busy || permissions === null}><RotateCcw className="mr-2 h-4 w-4" />Reset microphone &amp; camera permissions</Button>
      <p className="text-xs leading-relaxed text-cloud-500">Windows also needs to allow desktop apps to use the microphone: Windows Settings → Privacy &amp; security → Microphone → Let desktop apps access your microphone. Camera access has its own page.</p>
    </div>}
    {!nativeWindows && <p className="text-sm text-cloud-500">{desktop?.platform === "macos" ? "Manage Link's microphone and camera access in System Settings → Privacy & Security." : "Manage microphone and camera access in your browser's site settings. Link cannot change those settings for you."}</p>}
    {result && <p role={result.ok ? "status" : "alert"} className={`rounded-xl border p-4 text-sm leading-relaxed ${result.ok ? "border-emerald-500/30 text-cloud-700" : "border-rose-400/40 text-rose-500"}`}>{result.text}</p>}
    {import.meta.env.DEV && captureDetail && <p className="break-words text-xs text-cloud-500">Microphone diagnostic: {captureDetail}</p>}
  </section>;
}
